<template>
  <div class="date-picker" v-if="!loading">
    <md-date-combo-picker
      ref="comboPicker"
      v-model="show"
      :options="options"
      :active-header="activeTermIndex"
      :active-label="activeLabelIndex"
      :active-index="computedActiveIndex"
      :labels="labels"
      :headers="headers"
      :visible-item-count="visibleItemCount"
      @confirm="confirm"
      @cancel="cancel"
      @change-label="changeLabel"
      @change-header="changeHeader"
      @change-slide="changeSlide"
      class="common-date-picker"
      :class="{ 'without-header': withoutHeader }"
    ></md-date-combo-picker>
  </div>
</template>

<script>
import API from '@/api'
import { dingToast } from '@/utils/dingSdkUtils'
import { mcToast } from '@/utils/mcSdkUtils'
import {
  timeFormat,
  timeStrToDate,
  dateToTime,
  isWxPlatform
} from '@/utils/comUtils'
import { WEEK_DAY } from '@/utils/constUtils'

export default {
  components: {},
  props: {
    value: {
      type: Boolean,
      default: false
    },
    labelConfig: {
      type: Object,
      default() {
        return {}
      }
    },
    defaultIndex: {
      type: Array,
      default() {
        // 下标顺序分别为学期 标签 列（可能是多列 前两个之后的全是列下标）
        return []
      }
    },
    campusId: {
      type: String,
      default: ''
    },
    visibleItemCount: {
      type: Number,
      default: 3
    },
    withoutHeader: {
      type: Boolean,
      default: false
    },
    // 是否使用传入的 defaultIndex 学期
    isUseTermIndex: {
      type: Boolean,
      default: false
    },
    // 自定义的日期
    queryDay: {
      type: String,
      default: ''
    },
    //  当需要日期联动时，第一学期默认回显有问题，重新校准
    adjustingTermIndex: {
      type: Number,
      default: null
    },
    showCompleteDate: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      firstLoad: true,
      show: false,
      loading: true,
      activeLabelIndex: this.defaultIndex[1] || 0,
      activeWeekIndex: 0,
      activeMonthIndex: 0,
      activeTermIndex: this.defaultIndex[0] || 0,
      activeDateIndex: [0, 0],
      termChanged: false,
      headers: [],
      terms: [],
      defaultLabelConfig: {
        chooseDate: true,
        chooseWeek: true,
        chooseMonth: true,
        chooseTerm: true
      }
    }
  },
  computed: {
    labels() {
      const { chooseDate, chooseWeek, chooseMonth, chooseTerm } = Object.assign(
        {},
        this.defaultLabelConfig,
        this.labelConfig
      )
      let _labels = []
      if (chooseDate) {
        _labels.push({
          name: '选择日期',
          value: 'day',
          column: 2
        })
      }
      if (chooseWeek) {
        _labels.push({
          name: '选择学周',
          value: 'week',
          column: 1
        })
      }
      if (chooseMonth) {
        _labels.push({
          name: '选择月份',
          value: 'month',
          column: 1
        })
      }
      if (chooseTerm) {
        _labels.push({
          name: '学期统计',
          value: 'term',
          column: 1
        })
      }
      return _labels
    },
    options() {
      let _options = []
      if (this.terms.length) {
        let { date, week, month } = this.terms[this.activeTermIndex]
        let label = this.labels[this.activeLabelIndex]
        switch (label.value) {
          case 'day':
            // 如果当前时间小于学期开始时间 学期结束时间设置为学期开始时间 -- 应测试需求 如果下学期位开启，仅展示学期的第一天
            let now = +timeFormat(new Date(), 'YYYYMMDD')
            if (now < +((month || [])[0] || {}).startDimDay) {
              month = [month[0]]
            }
            _options = [month, date]
            break
          case 'week':
            _options = week
            break
          case 'month':
            _options = month
            break
          default:
            _options = [
              {
                ...this.headers[this.activeTermIndex],
                name: this.terms[this.activeTermIndex].termName
              }
            ]
            break
        }
      }
      return _options
    },
    computedActiveIndex() {
      let _active = 0
      // 设置默认值
      const {
        isCurrentTerm,
        defaultMonthIndex,
        defaultDateIndex,
        defaultWeekIndex,
        selectedMonthIndex,
        selectedWeekIndex,
        selectedDateIndex
      } = this.terms[this.activeTermIndex]
      let label = this.labels[this.activeLabelIndex]
      const dateIndex =
        isCurrentTerm || !this.termChanged
          ? this.activeDateIndex
          : selectedDateIndex || defaultDateIndex
      const monthIndex =
        isCurrentTerm || !this.termChanged
          ? this.activeMonthIndex
          : selectedMonthIndex || defaultMonthIndex
      const weekIndex =
        isCurrentTerm || !this.termChanged
          ? this.activeWeekIndex
          : selectedWeekIndex || defaultWeekIndex
      switch (label.value) {
        case 'day':
          _active = dateIndex
          break
        case 'month':
          _active = monthIndex
          break
        case 'week':
          _active = weekIndex
          break
        default:
          _active = 0
          break
      }
      return _active
    },
    currentRole() {
      return this.$store.getters.currentRole
    }
  },
  watch: {
    value(val) {
      this.show = val
    },
    show(val) {
      this.$emit('input', val)
    }
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {
    this.init()
  },
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    async init() {
      // 获取教学周
      try {
        let _terms = (await this.fetchTerms()) || []
        _terms = _terms.filter(
          item =>
            item &&
            item.schoolTermTeachWeekRespList &&
            item.schoolTermTeachWeekRespList.length
        )
        // 如果第一学期还未开始且存在两个学期
        if (
          _terms[0] &&
          _terms.length > 1 &&
          +timeFormat(new Date(), 'YYYYMMDD') <= _terms[0].schoolTermEndDimDay
        ) {
          _terms.splice(1)
        }
        const { firstEnd, termOrder } = this.$store.getters.semesterSetting
        let currentDateTerm = +termOrder
        if (this.queryDay) {
          currentDateTerm = this.queryDay <= firstEnd ? 1 : 2 // 当前日期的学期
        }
        // 重新校验学期 主要用于回显
        if (typeof this.adjustingTermIndex === 'number') {
          currentDateTerm = _terms[this.adjustingTermIndex].schoolTermOrder
        }
        this.terms = this.getTerms(_terms, currentDateTerm)
        this.headers = _terms.reduce((_list, item) => {
          return _list.concat({
            name: item.schoolTermOrder === 1 ? '第一学期' : '第二学期',
            endDimDay: item.schoolTermEndDimDay,
            startDimDay: item.schoolTermStartDimDay,
            termOrder: item.schoolTermOrder
          })
        }, [])
        // 设置默认值
        const {
          date,
          week,
          month,
          isCurrentTerm,
          defaultTermIndex,
          defaultMonthIndex,
          defaultDateIndex,
          defaultWeekIndex
        } = this.terms[this.activeTermIndex]
        let label = this.labels[this.activeLabelIndex]
        let defaultIndex = []
        let defaultValue = []
        const dateIndex = isCurrentTerm
          ? this.activeDateIndex
          : defaultDateIndex
        const monthIndex = isCurrentTerm
          ? this.activeMonthIndex
          : defaultMonthIndex
        const termIndex = isCurrentTerm
          ? this.activeTermIndex
          : defaultTermIndex
        const weekIndex = isCurrentTerm
          ? this.activeWeekIndex
          : defaultWeekIndex
        switch (label.value) {
          case 'day':
            defaultIndex = dateIndex
            defaultValue = [month[dateIndex[0]], date[dateIndex[1]]]
            break
          case 'month':
            defaultIndex = monthIndex
            defaultValue = month[monthIndex]
            break
          case 'week':
            defaultIndex = weekIndex
            defaultValue = week[weekIndex]
            break
          default:
            defaultIndex = termIndex
            defaultValue = this.headers[termIndex]
            break
        }
        const result = this.getEmitMap(defaultIndex, defaultValue)
        this.$emit('init-date', result)
        this.loading = false
        // 获取统计信息
      } catch (error) {
        if (isWxPlatform()) {
          return mcToast({ msg: error.message })
        }
        dingToast({ msg: error.message })
      }
      this.firstLoad = false
    },
    getTerms(_terms, currentDateTerm) {
      // 获取月份 根据学期的开始结束时间计算月份区间
      return _terms.reduce((list, item, termIndex) => {
        let current = timeStrToDate(
          `${Math.min(
            Math.max(
              +timeFormat(new Date(), 'YYYYMMDD'),
              item.schoolTermStartDimDay
            ),
            item.schoolTermEndDimDay
          )}`
        )
        let currentDay = +timeFormat(current, 'YYYYMMDD')
        let [defaultMonthIndex, defaultDateIndex] = this.defaultIndex.slice(2)
        // 判断是否当前学期当前学期需要确认当天在月，日、周三个维度下面的下标值
        const _isCurrentTerm = currentDateTerm === item.schoolTermOrder
        const activeLabel = this.labels[this.activeLabelIndex]
        let _month = this.calcMonth(
          item.schoolTermStartDimDay,
          item.schoolTermEndDimDay,
          _isCurrentTerm,
          item.schoolTermOrder
        )

        if (_isCurrentTerm && !this.isUseTermIndex) {
          this.activeTermIndex = termIndex
        }
        // 获取当前周对应的下标
        let _weekIndex = !_isCurrentTerm
          ? 0
          : /* eslint-disable */
            Math.max(
              0,
              item.schoolTermTeachWeekRespList.findIndex(
                item =>
                  currentDay >= item.teachWeekStartDimDay &&
                  currentDay <= item.teachWeekEndDimDay
              )
            )
        /* eslint-enable */
        const week = item.schoolTermTeachWeekRespList.reduce(
          (_weeks, week, index) => {
            let _start = timeFormat(
              timeStrToDate(`${week.teachWeekStartDimDay}`),
              'MM.DD'
            )
            let _end = timeFormat(
              timeStrToDate(`${week.teachWeekEndDimDay}`),
              'MM.DD'
            )
            if (index > _weekIndex && _isCurrentTerm) {
              return _weeks
            }
            // 当前时间之前的学周都展示出来
            return _weeks.concat({
              endDimDay: week.teachWeekEndDimDay,
              startDimDay: week.teachWeekStartDimDay,
              name: `第 ${index + 1} 周 (${_start} ~ ${_end})`
            })
          },
          []
        )
        /* eslint-disable */
        let __currentDay = currentDay
        let __current = current.getMonth()
        if (this.queryDay) {
          __current = new Date(dateToTime(this.queryDay, '.')).getMonth()
          __currentDay = this.queryDay
        }
        // 优先使用传入的默认下标
        defaultMonthIndex = _isCurrentTerm
          ? typeof defaultMonthIndex === 'number' &&
            ['day', 'month'].indexOf(activeLabel.value) > -1
            ? defaultMonthIndex
            : Math.max(
                _month.findIndex(item => __current === item.value),
                0
              )
          : 0

        let defaultWeekIndex = _isCurrentTerm
          ? typeof this.defaultIndex[2] === 'number' &&
            activeLabel.value === 'week'
            ? this.defaultIndex[2]
            : Math.max(
                week.findIndex(
                  item =>
                    __currentDay >= item.startDimDay &&
                    __currentDay <= item.endDimDay
                ),
                0
              )
          : 0

        const _date = this.calcMonthDate(
          item.schoolTermStartDimDay,
          item.schoolTermEndDimDay,
          _month[defaultMonthIndex].startDimDay,
          _isCurrentTerm,
          _month[defaultMonthIndex].value
        )
        let startDate = timeStrToDate(
          `${Math.min(
            Math.max(
              +timeFormat(current, 'YYYYMMDD'),
              item.schoolTermStartDimDay
            ),
            item.schoolTermEndDimDay
          )}`
        ).getDate()
        /* eslint-enable */
        defaultDateIndex = _isCurrentTerm
          ? typeof defaultDateIndex === 'number'
            ? defaultDateIndex
            : _date.findIndex(item => startDate === item.value)
          : 0
        defaultDateIndex = [defaultMonthIndex, defaultDateIndex]
        if (_isCurrentTerm) {
          // 处理默认值
          const _target = this.defaultIndex[2]
          const _date = this.defaultIndex[3]
          this.activeWeekIndex =
            typeof _target === 'undefined' ? defaultWeekIndex : _target
          this.activeMonthIndex =
            typeof _target === 'undefined' ? defaultMonthIndex : _target
          this.activeDateIndex =
            typeof _date === 'undefined' ? defaultDateIndex : [_target, _date]
          if (typeof this.activeTermIndex !== 'number') {
            this.activeTermIndex = typeof _target === 'undefined' ? 0 : _target
          }
        }
        /* eslint-disable */
        return list.concat({
          date: _date,
          week,
          month: _month,
          defaultMonthIndex,
          defaultWeekIndex,
          defaultDateIndex,
          selectedMonthIndex: !_isCurrentTerm
            ? 0
            : this.activeMonthIndex || defaultMonthIndex,
          selectedWeekIndex: !_isCurrentTerm
            ? 0
            : this.activeWeekIndex || defaultWeekIndex,
          selectedDateIndex: !_isCurrentTerm
            ? null
            : this.activeDateIndex || defaultDateIndex,
          defaultTermIndex: termIndex,
          isCurrentTerm: _isCurrentTerm,
          termName:
            _isCurrentTerm && !this.showCompleteDate
              ? `截至 ${timeFormat(current, 'MM.DD')}`
              : `${timeFormat(
                  timeStrToDate(String(item.schoolTermStartDimDay)),
                  'MM.DD'
                )} ~ ${timeFormat(
                  timeStrToDate(String(item.schoolTermEndDimDay)),
                  'MM.DD'
                )}`
        })
        /* eslint-enable */
      }, [])
    },
    calcMonth(termStartDimDay, termEndDimDay, _isCurrentTerm, schoolTermOrder) {
      let now = +timeFormat(new Date(), 'YYYYMMDD')
      // 当前处于第二学期 且开始日期如果和当前日期不是同一年 说明上一年的结束日期设置的有问题
      if (
        schoolTermOrder === 2 &&
        `${now}`.slice(0, 4) !== `${termStartDimDay}`.slice(0, 4)
      ) {
        now = timeStrToDate(`${termStartDimDay}`)
      }
      const termStartDate = timeStrToDate(`${termStartDimDay}`)
      const _startMonth = termStartDate.getMonth() + 1
      // 如果是当前学期 当天以后的日期不可见
      const _endMonth =
        timeStrToDate(
          `${
            _isCurrentTerm
              ? Math.max(Math.min(now, termEndDimDay), termStartDimDay)
              : termEndDimDay
          }`
        ).getMonth() + 1
      // 判断开始结束日期是否为同一年
      const yearGap =
        timeStrToDate(
          `${_isCurrentTerm ? Math.min(now, termEndDimDay) : termEndDimDay}`
        ).getFullYear() - termStartDate.getFullYear()
      const _month = new Array(yearGap * 12 + _endMonth - _startMonth + 1)
        .fill(null)
        .map((item, index) => {
          const _monthStartDate = new Date(
            termStartDate.getFullYear(),
            termStartDate.getMonth() + index,
            1
          )
          const _monthEndDate = new Date(
            termStartDate.getFullYear(),
            termStartDate.getMonth() + index + 1,
            0
          )
          item = {
            name: `${_monthStartDate.getMonth() + 1}月`,
            value: _monthStartDate.getMonth(),
            endDimDay: +timeFormat(_monthEndDate, 'YYYYMMDD'),
            startDimDay: +timeFormat(_monthStartDate, 'YYYYMMDD')
          }
          return item
        })
      return _month
    },
    calcMonthDate(
      termStartDimDay,
      termEndDimDay,
      monthStartDate,
      _isCurrentTerm,
      month
    ) {
      let _startDateStr = `${Math.min(
        Math.max(monthStartDate, termStartDimDay),
        termEndDimDay
      )}`
      let currDate = new Date()
      let currDateStr = +timeFormat(currDate, 'YYYYMMDD')
      let _startDate = timeStrToDate(_startDateStr)
      // 先把日期设置为下个月0号 也就是本月最后一天
      let _endDate = null
      if (_isCurrentTerm) {
        // 获取当月的天数
        _endDate = new Date(`${monthStartDate}`.substring(0, 4), month + 1, 0)
        let endDateStr = +timeFormat(_endDate, 'YYYYMMDD')
        if (currDateStr < termStartDimDay) {
          _endDate = timeStrToDate(
            `${Math.max(termStartDimDay, currDateStr, +_startDateStr)}`
          )
        } else if (currDateStr > termEndDimDay) {
          _endDate = timeStrToDate(
            `${Math.min(termEndDimDay, currDateStr, endDateStr)}`
          )
        } else {
          /* eslint-disable */
          _endDate =
            month === currDate.getMonth()
              ? timeStrToDate(`${Math.max(termStartDimDay, currDateStr)}`)
              : _endDate
          /* eslint-enable */
        }
      } else {
        _endDate = new Date(
          _startDate.getFullYear(),
          _startDate.getMonth() + 1,
          0
        )
        // 比较当月结束日期和学期开始日期
        _endDate = timeStrToDate(
          `${Math.min(
            Math.max(+timeFormat(_endDate, 'YYYYMMDD'), termStartDimDay),
            termEndDimDay
          )}`
        )
      }
      // 主要比较开始和结束时间
      const _startDateIndex = _startDate.getDate()
      // 如果是当前学期当月  就值渲染到当天结束
      const endDateIndex = _endDate.getDate()
      const _date = new Array(endDateIndex - _startDateIndex + 1)
        .fill(null)
        .map((item, index) => {
          const dimDay = new Date(
            _startDate.getFullYear(),
            _startDate.getMonth(),
            _startDateIndex + index
          )
          return {
            name: `${_startDateIndex + index}日（${
              WEEK_DAY[dimDay.getDay()]
            }）`,
            value: _startDateIndex + index,
            dimDay: +timeFormat(new Date(dimDay), 'YYYYMMDD')
          }
        })
      return _date
    },
    confirm({ headerIndex, labelIndex, activeIndex, activeValue } = {}) {
      // 非当前学期的selectedMonthIndex,selectedWeekIndex,selectedDateIndex 都重置为0
      this.activeTermIndex = headerIndex
      this.activeLabelIndex = labelIndex
      // 当前学期
      let term = this.terms[headerIndex]
      let label = this.labels[this.activeLabelIndex]

      switch (label.value) {
        case 'day':
          term.selectedWeekIndex = 0
          term.selectedMonthIndex = 0
          term.selectedDateIndex = this.activeDateIndex = activeIndex
          break
        case 'month':
          term.selectedDateIndex = null
          term.selectedWeekIndex = 0
          term.selectedMonthIndex = this.activeMonthIndex = activeIndex
          break
        case 'week':
          term.selectedDateIndex = null
          term.selectedMonthIndex = 0
          term.selectedWeekIndex = this.activeWeekIndex = activeIndex
          break
      }
      this.terms = this.terms.reduce((list, item, termIndex) => {
        if (termIndex === headerIndex) {
          list.push(term)
        } else {
          list.push({
            ...item,
            selectedMonthIndex: 0,
            selectedWeekIndex: 0,
            selectedDateIndex: null
          })
        }
        return list
      }, [])
      const result = this.getEmitMap(activeIndex, activeValue)
      this.show = false
      this.termChanged = false
      this.$emit('confirm', result)
    },
    getEmitMap(activeIndex, activeValue) {
      let defaultIndex = [this.activeTermIndex, this.activeLabelIndex]
      let label = this.labels[this.activeLabelIndex]
      switch (label.value) {
        case 'day':
          defaultIndex = defaultIndex.concat(activeIndex)
          this.dateStr = `${('0' + (activeValue[0].value + 1)).slice(-2)}.${(
            '0' + activeValue[1].value
          ).slice(-2)} ${
            WEEK_DAY[timeStrToDate(activeValue[1].dimDay + '').getDay()]
          }`
          break
        case 'term':
          defaultIndex.push(activeIndex)
          this.dateStr = `${this.headers[this.activeTermIndex].name}（${
            this.terms[this.activeTermIndex].termName
          }）`
          break
        default:
          defaultIndex.push(activeIndex)
          this.dateStr = `${activeValue.name}`
          break
      }
      const { startDimDay, endDimDay } =
        Object.prototype.toString.call(activeValue) === '[object Array]'
          ? activeValue[0]
          : activeValue
      return {
        activeIndex: defaultIndex,
        activeValue: {
          labelType: label.value,
          startDimDay,
          endDimDay,
          dimDay: (activeValue[1] || {}).dimDay
        },
        dateStr: this.dateStr
      }
    },
    cancel({ columnIndex, headerIndex, labelIndex } = {}) {
      this.termChanged = false
      this.activeLabelIndex = labelIndex
      this.activeTermIndex = headerIndex
      let label = this.labels[labelIndex]
      if (label.value === 'day') {
        this.activeDateIndex = columnIndex
      } else if (label.value === 'week') {
        this.activeWeekIndex = columnIndex
      } else if (label.value === 'month') {
        this.activeMonthIndex = columnIndex
      }
      this.$emit('input', false)
      this.show = false
    },
    changeSlide({ columnIndex, columnValue, labelIndex } = {}) {
      // eslint-disable-next-line
      const { endDimDay, startDimDay } = this.headers[this.activeTermIndex]
      const { isCurrentTerm } = this.terms[this.activeTermIndex]
      let label = this.labels[labelIndex]
      // 月份发生变化 就自动切换对应的日期 把term[index].date替换成当前月份的date
      if (label.value === 'day' && !columnIndex) {
        const _dates = this.calcMonthDate(
          startDimDay,
          endDimDay,
          columnValue.startDimDay,
          isCurrentTerm,
          columnValue.value
        )
        this.$set(this.terms, this.activeTermIndex, {
          ...this.terms[this.activeTermIndex],
          date: _dates
        })
      }
    },
    changeLabel(labelIndex) {
      this.activeLabelIndex = labelIndex
    },
    changeHeader(headerIndex) {
      this.termChanged = headerIndex !== this.activeTermIndex
      this.activeTermIndex = headerIndex
    },
    // 获取学期周
    fetchWeekList() {
      return new Promise((resolve, reject) => {
        API.getFamilyNaturalWeek({
          operUserRoleTypes: `${this.currentRole}`,
          campusId: this.campusId
        })
          .then(res => {
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    fetchTerms() {
      return new Promise((resolve, reject) => {
        API.getYearTeachWeek({
          operUserRoleTypes: `${this.currentRole}`,
          campusId: this.campusId,
          schoolYear: this.$store.getters.semesterSetting.schoolYear
        })
          .then(res => {
            if (res && res.result && res.result !== '0') {
              resolve([])
            } else {
              resolve(res)
            }
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    // 未使用？
    fetchStatUserDetail(type = this.$route.query.type) {
      return new Promise((resolve, reject) => {
        API[!type ? 'getStatUserDetail' : 'getStatUserSummary']({
          operUserRoleTypes: `${this.currentRole}`,
          campusId: this.campusId,
          startDimDay: +this.$route.query.startDimDay,
          endDimDay: +this.$route.query.endDimDay,
          stuDingId: this.$route.query.endDimDay.stuDingId
        })
          .then(res => {
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    }
  }
}
</script>

<style lang="scss">
.common-date-picker {
  &.without-header {
    .md-date-combo-picker__header {
      display: none;
    }
  }
}
</style>
